import { z } from 'zod';

/**
 * Rung 3 of the customization ladder: **the bundle ships a section registry;
 * config selects and arranges** (spec §6.4.3).
 *
 * This is the second half of the theme contract — the first is
 * `../theme/contract.ts`. Together they are the whole of what
 * `tenant_config.config.branding` may address, which is what makes rule 1 ("the
 * shared bundle is never edited for one tenant") mechanical rather than
 * aspirational: a request that cannot be expressed as a preset, a token, a
 * section arrangement or a slot value is a rung-5 engagement by definition,
 * and there is nowhere in this file for it to be quietly accommodated.
 *
 * ## The shape
 *
 * A section is `{ type, id?, slots? }`. `type` picks a block from the registry;
 * `id` is optional and only ever a React key and an anchor, so a page may carry
 * two galleries; `slots` is validated by that block's own schema, **with every
 * field defaulted**, so `slots: {}` and no `slots` at all are the same thing and
 * a half-filled block still renders.
 *
 * ## Why an unknown section type is skipped rather than fatal
 *
 * The same reason an unknown token is ignored: one static build serves every
 * hostname (§6.4.2), so a branding row published against next week's bundle is
 * ordinary traffic. A page missing a block a tenant added yesterday is a small
 * disappointment; a blank page on a live booking site is an outage.
 *
 * ## Content zones (rung 4)
 *
 * Slots typed `richtext` carry **server-sanitized** HTML — §6.4.3: "sanitized
 * HTML/markdown (no scripts, server-sanitized)". Everything else is plain text
 * and is escaped by React in the ordinary way. `RichText` in `blocks.tsx` is the
 * only component in this app that calls `dangerouslySetInnerHTML`, it says so,
 * and it carries a tripwire that refuses anything script-shaped — because the
 * sanitizer is server-side and does not exist yet, and a defence that is one
 * deploy away from being the only one should fail loudly rather than trust.
 */

/* ------------------------------------------------------------------ */
/* Slot vocabulary                                                     */
/* ------------------------------------------------------------------ */

/** A short line of plain text. Escaped by React; never HTML. */
const Text = (fallback = '') => z.string().max(400).catch(fallback).default(fallback);
/** A paragraph of plain text. */
const Prose = (fallback = '') => z.string().max(4000).catch(fallback).default(fallback);
/** A content zone: server-sanitized HTML. See `RichText`. */
const RichText = z.string().max(20_000).catch('').default('');
const Flag = (fallback: boolean) => z.boolean().catch(fallback).default(fallback);

const Image = z.object({
  src: z.string().max(2000).catch('').default(''),
  /** Required by the contract even when empty — a decorative image says so. */
  alt: z.string().max(300).catch('').default(''),
  caption: Text(),
});
export type Image = z.infer<typeof Image>;

const Row = z.object({ label: Text(), value: Text() });
export type Row = z.infer<typeof Row>;

/* ------------------------------------------------------------------ */
/* The blocks                                                          */
/* ------------------------------------------------------------------ */

/**
 * `hero` — the first screen. `headline` defaults to empty because the tenant's
 * *name* is the honest fallback and it comes from `/api/site`, not from config:
 * a site with no branding row still says who it is.
 */
export const HeroSlots = z.object({
  headline: Text(),
  sub: Prose(),
  cta_label: Text('Book a time'),
  align: z.enum(['left', 'center']).catch('left').default('left'),
  /** Overrides `image.hero` for this block only. */
  image: z.string().max(2000).catch('').default(''),
});

/**
 * `services` — the catalog, from `/api/site`. Its content is **not** config:
 * prices come from the catalog and nowhere else (B7), so the slots here choose
 * how the list looks and what it is introduced by, never what is in it.
 */
export const ServicesSlots = z.object({
  headline: Text('What we offer'),
  note: Prose(),
  layout: z.enum(['list', 'grid']).catch('list').default('list'),
  show_price: Flag(true),
  show_duration: Flag(true),
});

/**
 * `hours` — opening times from the book, with the owner's words on top.
 *
 * `/api/site` projects `tenant_config.hours` (the row the scheduling engine
 * reads), so the block's *default* content is now the same week that decides
 * what is bookable: change Sunday once and the page changes with it. That is
 * the whole reason the projection landed — a website advertising hours the book
 * no longer keeps is worse than one with no hours at all.
 *
 * `rows` stays, and stays an **override**: an owner who types rows gets exactly
 * those, because "Sun–Mon closed, kitchen till 9" is a sentence and no
 * projection of a `DayHours[]` will ever be one. `note` is additive prose —
 * "Holidays by appointment" — and prints under either source.
 */
export const HoursSlots = z.object({
  headline: Text('Hours'),
  rows: z.array(Row).max(14).catch([]).default([]),
  note: Prose(),
});

/** `story` — the words. A content zone (rung 4) with a plain-text fallback. */
export const StorySlots = z.object({
  headline: Text('Our story'),
  /** Plain text, escaped. Used when `body_html` is empty. */
  body: Prose(),
  /** Server-sanitized HTML. Wins over `body` when present. */
  body_html: RichText,
  image: z.string().max(2000).catch('').default(''),
});

export const GallerySlots = z.object({
  headline: Text(),
  images: z.array(Image).max(24).catch([]).default([]),
});

/**
 * `booking` — where the flow mounts.
 *
 * A page whose config omits it still books: the shell offers the flow anyway
 * (see `DEFAULT_ARRANGEMENT` and `App.tsx`). A booking site that a tenant could
 * accidentally configure into being unable to take a booking would be a footgun
 * dressed as flexibility.
 */
export const BookingSlots = z.object({
  headline: Text('Book a time'),
  note: Prose(),
});

export const FooterSlots = z.object({
  note: Prose(),
  rows: z.array(Row).max(8).catch([]).default([]),
  /** Server-sanitized HTML — the place a tenant puts their own fine print. */
  html: RichText,
});

export interface SectionSpec {
  label: string;
  slots: z.ZodType<Record<string, unknown>>;
  /** Why the block exists, in the vocabulary the admin console will reuse. */
  why: string;
}

export const SECTIONS = {
  hero: {
    label: 'Hero',
    slots: HeroSlots,
    why: 'The first screen: the name, one line, and the way in to booking.',
  },
  services: {
    label: 'Services',
    slots: ServicesSlots,
    why: 'The bookable catalog from /api/site. Prices are the catalog’s (B7).',
  },
  hours: {
    label: 'Hours',
    slots: HoursSlots,
    why: 'Opening times from the tenant’s own week; typed rows override them.',
  },
  story: {
    label: 'Story',
    slots: StorySlots,
    why: 'A content zone (rung 4) — the tenant’s own words, server-sanitized.',
  },
  gallery: {
    label: 'Gallery',
    slots: GallerySlots,
    why: 'Their photography, which §6.4.3 says does more than bespoke code.',
  },
  booking: {
    label: 'Booking',
    slots: BookingSlots,
    why: 'Where the availability → OTP → confirm flow mounts.',
  },
  footer: {
    label: 'Footer',
    slots: FooterSlots,
    why: 'Address, telephone, and the tenant’s own fine print.',
  },
} as const satisfies Record<string, SectionSpec>;

export type SectionType = keyof typeof SECTIONS;

export const SECTION_TYPES = Object.keys(SECTIONS) as SectionType[];

export function isSectionType(value: string): value is SectionType {
  return Object.prototype.hasOwnProperty.call(SECTIONS, value);
}

/**
 * What a tenant who has configured nothing gets: say who you are, show what you
 * sell, take the booking, print the address. Four blocks, in the order a
 * stranger reads them.
 */
export const DEFAULT_ARRANGEMENT: readonly SectionType[] = [
  'hero',
  'services',
  'booking',
  'footer',
];

export interface ArrangedSection {
  /** React key and anchor id — `hero`, `gallery-2`. */
  key: string;
  type: SectionType;
  /** Parsed against the block's own schema, so every field is present. */
  slots: Record<string, unknown>;
}

export interface Arrangement {
  sections: ArrangedSection[];
  warnings: string[];
}

/**
 * Turn `branding.sections` into the page.
 *
 * Accepts three shapes, because a config document written by a person and one
 * written by a form should both work:
 *
 *  - absent / empty  → `DEFAULT_ARRANGEMENT`
 *  - `["hero", "gallery"]`  → those blocks, default slots
 *  - `[{ type: "hero", slots: { … } }, …]`  → the full form
 *
 * Everything it cannot use is skipped with a warning, and if *nothing* survives
 * it falls back to the default arrangement rather than rendering a page with no
 * content — the same "a booking site must always be able to take a booking"
 * rule that keeps `booking` in the default list.
 */
export function arrangeSections(branding: unknown): Arrangement {
  const warnings: string[] = [];
  const config = isRecord(branding) ? branding : {};
  const raw = config['sections'];

  if (raw === undefined) return { sections: defaults(), warnings };
  if (!Array.isArray(raw)) {
    warnings.push('branding.sections is not a list; the default arrangement was used.');
    emit(warnings);
    return { sections: defaults(), warnings };
  }

  const seen = new Map<SectionType, number>();
  const sections: ArrangedSection[] = [];

  for (const [index, entry] of raw.entries()) {
    const parsed = readEntry(entry, index, warnings);
    if (parsed === null) continue;

    const count = (seen.get(parsed.type) ?? 0) + 1;
    seen.set(parsed.type, count);

    sections.push({
      key: parsed.id ?? (count === 1 ? parsed.type : `${parsed.type}-${String(count)}`),
      type: parsed.type,
      slots: SECTIONS[parsed.type].slots.parse(parsed.slots),
    });
  }

  if (sections.length === 0) {
    warnings.push('branding.sections named no usable block; the default arrangement was used.');
    emit(warnings);
    return { sections: defaults(), warnings };
  }

  emit(warnings);
  return { sections, warnings };
}

function readEntry(
  entry: unknown,
  index: number,
  warnings: string[],
): { type: SectionType; id?: string; slots: unknown } | null {
  if (typeof entry === 'string') {
    if (!isSectionType(entry)) {
      warnings.push(`unknown section "${entry}" at position ${String(index)} was skipped.`);
      return null;
    }
    return { type: entry, slots: {} };
  }

  if (!isRecord(entry)) {
    warnings.push(`section at position ${String(index)} is not an object; it was skipped.`);
    return null;
  }

  const type = entry['type'];
  if (typeof type !== 'string' || !isSectionType(type)) {
    warnings.push(
      `unknown section "${String(type)}" at position ${String(index)} was skipped (theme contract v1).`,
    );
    return null;
  }

  const id = entry['id'];
  return {
    type,
    ...(typeof id === 'string' && /^[a-z0-9-]{1,40}$/i.test(id) ? { id } : {}),
    slots: entry['slots'] ?? {},
  };
}

function defaults(): ArrangedSection[] {
  return DEFAULT_ARRANGEMENT.map((type) => ({
    key: type,
    type,
    slots: SECTIONS[type].slots.parse({}),
  }));
}

function emit(warnings: string[]): void {
  for (const warning of warnings) console.warn(`[sections] ${warning}`);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
